import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import EposCheckout from "./EposCheckout";
import EposReceipt from "./EposReceipt";
import {
  Search, X, Plus, Minus, Trash2, ShoppingCart, ArrowLeft, Loader2,
  Star, PackageX, ImageOff, Layers, ScanLine, BadgeCheck, AlertTriangle,
  Volume2, VolumeX, Banknote, Lock, Download, Mail, FileText,
  ArrowDownCircle, ArrowUpCircle, CheckCircle2, Printer,
} from "lucide-react";

const money = (n) => (n === null || n === undefined || n === "" ? "—" : `£${Number(n).toFixed(2)}`);
const isOut = (s) => s === "outofstock" || s === "out_of_stock";

let _audioCtx;
function beep(kind) {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("epos_scan_sound") === "0") return;
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    const o = _audioCtx.createOscillator(), g = _audioCtx.createGain();
    o.connect(g); g.connect(_audioCtx.destination);
    o.type = "square";
    o.frequency.value = kind === "success" ? 880 : kind === "warn" ? 520 : 200;
    g.gain.value = 0.04;
    o.start();
    o.stop(_audioCtx.currentTime + (kind === "success" ? 0.08 : 0.16));
  } catch (e) { /* audio optional */ }
}

function isEditableTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable === true;
}

function ProductImage({ src, alt }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return <div className="w-full aspect-square rounded-md bg-slate-100 flex items-center justify-center text-slate-300"><ImageOff className="w-6 h-6" /></div>;
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setErr(true)}
    className="w-full aspect-square object-cover rounded-md bg-slate-50" />;
}

export default function EposRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const registerId = params.get("register");

  const [cfg, setCfg] = useState(null);
  const [register, setRegister] = useState(null);
  const [session, setSession] = useState(null);
  const [activeCat, setActiveCat] = useState("");
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [booting, setBooting] = useState(true);
  const [basket, setBasket] = useState([]);
  const [quickKeys, setQuickKeys] = useState([]);
  const [variationFor, setVariationFor] = useState(null);
  const [variations, setVariations] = useState([]);
  const [loadingVars, setLoadingVars] = useState(false);
  const [source, setSource] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [sessionTotals, setSessionTotals] = useState(null);
  // Phase 5 — cash management, close register, Z-report
  const [cashOpen, setCashOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [movementForm, setMovementForm] = useState({ type: "cash_in", amount: "", reason: "" });
  const [savingMovement, setSavingMovement] = useState(false);
  const [countedCash, setCountedCash] = useState("");
  const [discrepancyReason, setDiscrepancyReason] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [zReport, setZReport] = useState(null);
  const searchRef = useRef(null);
  const [lastScan, setLastScan] = useState(null);
  const [soundOn, setSoundOn] = useState(() => (typeof localStorage !== "undefined" ? localStorage.getItem("epos_scan_sound") !== "0" : true));
  const toggleSound = () => setSoundOn((s) => { const n = !s; try { localStorage.setItem("epos_scan_sound", n ? "1" : "0"); } catch (e) {} return n; });
  const scanHandlerRef = useRef(null);
  const scanClearTimer = useRef(null);

  const loadSessionTotals = useCallback(async () => {
    try {
      const r = await api.get(`/epos/registers/${registerId}/session/totals`);
      setSessionTotals(r.data.totals);
    } catch (e) { /* ignore */ }
  }, [registerId]);

  const loadRecon = useCallback(async () => {
    setReconLoading(true);
    try {
      const r = await api.get(`/epos/registers/${registerId}/session/reconciliation`);
      setRecon(r.data.reconciliation);
    } catch (e) { /* ignore */ } finally { setReconLoading(false); }
  }, [registerId]);

  const openCash = async () => { setCashOpen(true); await loadRecon(); };

  const submitMovement = async () => {
    const amt = Number(movementForm.amount);
    if (!(amt > 0)) { toast.error("Enter an amount greater than zero"); return; }
    if (!movementForm.reason.trim()) { toast.error("A reason is required for a cash movement"); return; }
    setSavingMovement(true);
    try {
      await api.post(`/epos/registers/${registerId}/cash-movement`, {
        type: movementForm.type, amount: amt, reason: movementForm.reason.trim() });
      toast.success(`${movementForm.type === "cash_in" ? "Cash in" : "Cash out"} recorded`);
      setMovementForm((f) => ({ ...f, amount: "", reason: "" }));
      await Promise.all([loadRecon(), loadSessionTotals()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to record movement"); }
    finally { setSavingMovement(false); }
  };

  const openCloseFlow = async () => {
    setCountedCash(""); setDiscrepancyReason(""); setConfirmClose(false);
    setCloseOpen(true); await loadRecon();
  };

  const doClose = async () => {
    if (countedCash === "" || isNaN(Number(countedCash))) { toast.error("Enter the counted cash"); return; }
    const disc = Number(countedCash) - Number(recon?.expected_cash || 0);
    if (Math.abs(disc) > 0.005 && !discrepancyReason.trim()) {
      toast.error("A reason is required when the count differs from expected"); return;
    }
    setClosing(true);
    try {
      const r = await api.post(`/epos/registers/${registerId}/close`, {
        counted_cash: Number(countedCash),
        discrepancy_reason: discrepancyReason.trim() || undefined });
      setZReport(r.data.z_report);
      setSession(null);
      setCloseOpen(false); setConfirmClose(false);
      toast.success("Register closed — Z-report ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to close register"); setConfirmClose(false); }
    finally { setClosing(false); }
  };

  const downloadZpdf = async () => {
    try {
      const r = await api.get(`/epos/sessions/${zReport.session_id}/z-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ZReport-${zReport.session_ref || zReport.session_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const emailZreport = async () => {
    try {
      const r = await api.post("/epos/reports/email", { kind: "zreport", session_id: zReport.session_id });
      toast.success(`Emailed to ${(r.data.sent_to || []).join(", ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to email Z-report"); }
  };

  // Print the EXISTING immutable Z-report on an 80mm receipt (or browser print fallback).
  // Reads only the in-memory snapshot — never re-fetches or regenerates financial figures,
  // and never posts (so it cannot re-close the register or alter records).
  const printZreport = () => {
    const z = zReport;
    if (!z) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const g = (n) => (n === null || n === undefined ? "—" : `£${Number(n).toFixed(2)}`);
    const store = cfg?.stores?.find((s) => s.id === register?.store_id)?.name || "";
    const row = (k, v, cls = "") => `<tr class="${cls}"><td>${esc(k)}</td><td class="r">${v}</td></tr>`;
    const movements = (z.cash_movements || []).map((m) =>
      row(`${m.type === "cash_in" ? "Cash in" : "Cash out"}: ${m.reason}`,
          `${m.type === "cash_in" ? "+" : "−"}${g(m.amount)}`)).join("");
    const sps = (z.salespersons || []).map((s) =>
      row(s.salesperson, `${s.transaction_count} · ${g(s.net_sales)}`)).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Z-Report ${esc(z.session_ref || z.session_id)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', ui-monospace, monospace; font-size: 12px; color:#000; margin:0; padding:2px; width:74mm; }
  h1 { font-size: 14px; text-align:center; margin:2px 0 0; letter-spacing:.5px; }
  .muted { text-align:center; font-size:10px; color:#222; margin:0; }
  hr { border:none; border-top:1px dashed #000; margin:6px 0; }
  table { width:100%; border-collapse:collapse; }
  td { padding:1px 0; vertical-align:top; word-break:break-word; }
  td.r { text-align:right; white-space:nowrap; padding-left:6px; }
  .sec { font-weight:bold; text-transform:uppercase; font-size:11px; margin:6px 0 2px; }
  tr.big td { font-weight:bold; font-size:13px; }
</style></head><body>
  ${store ? `<h1>${esc(store)}</h1>` : ""}
  <p class="muted">END-OF-DAY Z-REPORT</p>
  <p class="muted">${esc(z.session_ref || z.session_id)}</p>
  <hr>
  <table>
    ${row("Register", esc(register?.name || z.register_id))}
    ${row("Opened", `${esc((z.opened_at || "").slice(0, 16).replace("T", " "))}`)}
    ${row("By", esc(z.opened_by_name || "—"))}
    ${row("Closed", `${esc((z.closed_at || "").slice(0, 16).replace("T", " "))}`)}
    ${row("By", esc(z.closed_by_name || "—"))}
  </table>
  <hr>
  <div class="sec">Sales</div>
  <table>
    ${row("Transactions", String(z.transactions ?? 0))}
    ${row("Gross sales", g(z.gross_sales))}
    ${row("Discounts", g(z.discounts))}
    ${row("VAT", g(z.vat))}
    ${row("Refunds", g(z.refunds))}
    ${row("Net sales", g(z.net_sales), "big")}
    ${row("Voids / cancelled", String(z.voids ?? 0))}
  </table>
  <hr>
  <div class="sec">Payments</div>
  <table>
    ${row("Cash", g(z.cash_sales))}
    ${row("Card", g(z.card_sales))}
  </table>
  <hr>
  <div class="sec">Cash reconciliation</div>
  <table>
    ${row("Opening float", g(z.opening_float))}
    ${row("Cash sales", g(z.cash_sales))}
    ${row("Cash refunds", g(z.cash_refunds))}
    ${row("Cash in", g(z.cash_in))}
    ${row("Cash out", g(z.cash_out))}
    ${row("Expected cash", g(z.expected_cash), "big")}
    ${row("Counted cash", z.counted_cash != null ? g(z.counted_cash) : "—")}
    ${row("Discrepancy", z.cash_discrepancy != null ? g(z.cash_discrepancy) : "—")}
  </table>
  ${z.discrepancy_reason ? `<p class="muted" style="text-align:left">Reason: ${esc(z.discrepancy_reason)}</p>` : ""}
  ${movements ? `<hr><div class="sec">Cash movements</div><table>${movements}</table>` : ""}
  ${sps ? `<hr><div class="sec">Salespersons</div><table>${sps}</table>` : ""}
  <hr>
  <p class="muted">TEST MODE — immutable snapshot</p>
  <p class="muted">Not a VAT invoice</p>
  <p class="muted">Generated ${esc((z.generated_at || z.closed_at || "").slice(0, 19).replace("T", " "))}</p>
</body></html>`;
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) { toast.error("Allow pop-ups to print the Z-report"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* fallback: window stays open for manual print */ } }, 300);
  };

  const loadQuickKeys = useCallback(async () => {
    try {
      const r = await api.get("/epos/quick-keys", { params: { register_id: registerId } });
      setQuickKeys(r.data.items || []);
    } catch (e) { /* ignore */ }
  }, [registerId]);

  useEffect(() => {
    (async () => {
      try {
        const c = await api.get("/epos/config");
        setCfg(c.data);
        const reg = (c.data.registers || []).find((r) => r.id === registerId);
        if (!reg) { toast.error("Register not found"); navigate("/epos"); return; }
        setRegister(reg);
        let s = (await api.get(`/epos/registers/${registerId}/session`)).data.session;
        if (!s && c.data.permissions?.sell && reg.status === "active") {
          try { s = (await api.post(`/epos/registers/${registerId}/open`, { opening_float: 0 })).data.session; } catch (e) { /* ignore */ }
        }
        setSession(s);
        await loadQuickKeys();
        await loadSessionTotals();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load register");
        navigate("/epos");
      } finally {
        setBooting(false);
      }
    })();
  }, [registerId, navigate, loadQuickKeys]);

  const fetchProducts = useCallback(async (reset) => {
    setLoadingProducts(true);
    try {
      const nextPage = reset ? 1 : page;
      const r = await api.get("/epos/catalogue/products", { params: { q, category: activeCat, page: nextPage } });
      setSource(r.data.source + (r.data.stale ? " (offline cache)" : ""));
      if (r.data.warning) toast.warning("Catalogue temporarily using cached data");
      setTotalPages(r.data.total_pages || 1);
      setProducts((prev) => reset ? (r.data.items || []) : [...prev, ...(r.data.items || [])]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load products");
    } finally { setLoadingProducts(false); }
  }, [q, activeCat, page]);

  useEffect(() => {
    if (booting) return;
    setPage(1);
    const t = setTimeout(() => fetchProducts(true), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activeCat, booting]);

  const loadMore = () => {
    const np = page + 1;
    setPage(np);
    setTimeout(() => fetchProducts(false), 0);
  };

  const flashScan = (status, msg) => {
    setLastScan({ status, msg, at: Date.now() });
    if (scanClearTimer.current) clearTimeout(scanClearTimer.current);
    scanClearTimer.current = setTimeout(() => setLastScan(null), 2600);
  };

  // Core scan/lookup: identify by SKU/barcode/EAN, check eligibility, add to basket, feedback.
  const processScan = async (code, { lenient = false } = {}) => {
    const term = (code || "").trim();
    if (!term) return;
    if (!canSell) { flashScan("error", "Not permitted to sell on this register"); beep("error"); return; }
    try {
      let items = [];
      try {
        const lr = await api.get("/epos/catalogue/local-lookup", { params: { code: term } });
        items = lr.data.items || [];
      } catch (e) { /* fall back to catalogue search */ }
      if (!items.length) {
        const r = await api.get("/epos/catalogue/products", { params: { q: term, page: 1 } });
        items = r.data.items || [];
      }
      let match = items.find((p) => (p.sku || "").toLowerCase() === term.toLowerCase());
      if (!match && (lenient || items.length === 1) && items.length >= 1) match = items[0];
      if (!match) {
        if (items.length > 1) flashScan("error", `Ambiguous code "${term}" — ${items.length} matches`);
        else flashScan("error", `Unknown barcode "${term}"`);
        beep("error"); setProducts(items); return;
      }
      if (isOut(match.stock_status)) { flashScan("warn", `Out of stock: ${match.name}`); beep("error"); return; }
      if (match.type_variable) {
        openVariations(match);
        flashScan("warn", `${match.name} has options — choose a variation`); beep("warn"); return;
      }
      addProduct(match);
      flashScan("success", `Added: ${match.name}`);
      beep("success");
    } catch (e) {
      flashScan("error", "Scan lookup failed"); beep("error");
    }
  };

  // Manual search box: Enter adds the best match (lenient) and clears the field.
  const handleScan = async () => {
    const term = q.trim();
    if (!term) return;
    await processScan(term, { lenient: true });
    setQ("");
    searchRef.current?.focus();
  };

  // Keep the global scanner handler pointing at the latest closure (avoids stale state).
  useEffect(() => { scanHandlerRef.current = processScan; });

  // Always-on HID/Bluetooth scanner: capture rapid keystroke bursts anywhere on the register
  // EXCEPT while typing in an editable field. Completes on an Enter/Tab suffix or a short idle
  // gap (for suffix-less scanners). Never hijacks normal typing; prevents duplicate processing.
  useEffect(() => {
    let buf = "", last = 0, flushTimer = null;
    const RESET_MS = 120, MIN_LEN = 3;
    const finalize = () => {
      const code = buf; buf = "";
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
      if (code.length >= MIN_LEN && scanHandlerRef.current) scanHandlerRef.current(code, { lenient: false });
    };
    const onKey = (e) => {
      if (isEditableTarget(e.target)) return;          // don't interfere with fields
      const now = Date.now();
      if (e.key === "Enter" || e.key === "Tab") {
        if (buf.length >= MIN_LEN) { e.preventDefault(); finalize(); } else { buf = ""; }
        return;
      }
      if (e.key && e.key.length === 1) {
        if (now - last > RESET_MS) buf = "";
        buf += e.key; last = now;
        if (flushTimer) clearTimeout(flushTimer);
        flushTimer = setTimeout(() => { if (buf.length >= 8) finalize(); else buf = ""; }, 140);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); if (flushTimer) clearTimeout(flushTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLine = (line) => {
    if (isOut(line.stock_status)) { toast.error("Item is out of stock"); return; }
    setBasket((b) => {
      const i = b.findIndex((x) => x.key === line.key);
      if (i >= 0) { const c = [...b]; c[i] = { ...c[i], qty: c[i].qty + 1 }; return c; }
      return [...b, { ...line, qty: 1 }];
    });
  };

  const addProduct = async (p) => {
    if (p.type_variable) { openVariations(p); return; }
    if (p.price_missing || p.price == null || Number(p.price) <= 0) {
      flashScan("warn", `No price set for ${p.name} — cannot sell until a price is added`);
      beep("warn");
      return;
    }
    addLine({ key: `p${p.wc_id || p.htss_legacy_id || p.sku || p.name}`, name: p.name, price: p.price || 0,
              image: p.image, sku: p.sku, wc_id: p.wc_id, stock_status: p.stock_status,
              product_legacy_id: p.htss_legacy_id || null, is_bundle: !!p.is_bundle });
  };

  const openVariations = async (p) => {
    setVariationFor(p); setVariations([]); setLoadingVars(true);
    try {
      const r = await api.get(`/epos/catalogue/products/${p.wc_id}/variations`);
      setVariations(r.data.items || []);
    } catch (e) { toast.error("Could not load variations"); } finally { setLoadingVars(false); }
  };

  const addVariation = (p, v) => {
    addLine({ key: `v${v.wc_variation_id}`, name: `${p.name} — ${v.name}`, price: v.price || 0,
              image: v.image || p.image, sku: v.sku, wc_id: p.wc_id, wc_variation_id: v.wc_variation_id,
              stock_status: v.stock_status });
    setVariationFor(null);
  };

  const addQuickKey = (k) => addLine({ key: `q${k.id}`, name: k.title, price: k.price || 0,
    image: k.image, sku: k.sku, wc_id: k.wc_id, wc_variation_id: k.wc_variation_id, stock_status: "instock" });

  const pinToQuickKeys = async (p, e) => {
    e.stopPropagation();
    try {
      await api.post("/epos/quick-keys", { title: p.name, wc_id: p.wc_id, sku: p.sku,
        price: p.price, image: p.image, type: p.type });
      toast.success("Added to quick keys");
      loadQuickKeys();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const setQty = (key, delta) => setBasket((b) =>
    b.map((x) => x.key === key ? { ...x, qty: Math.max(1, x.qty + delta) } : x));
  const removeLine = (key) => setBasket((b) => b.filter((x) => x.key !== key));

  const totals = useMemo(() => {
    const subtotal = basket.reduce((s, x) => s + (x.price || 0) * x.qty, 0);
    return { subtotal, count: basket.reduce((s, x) => s + x.qty, 0) };
  }, [basket]);

  if (booting) {
    return <div className="h-screen flex items-center justify-center bg-slate-900 text-slate-300 gap-2">
      <Loader2 className="w-5 h-5 animate-spin" /> Loading register…
    </div>;
  }

  const canSell = cfg?.permissions?.sell;
  const canManage = cfg?.permissions?.manage;

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden" data-testid="epos-register-screen">
      {/* Top bar */}
      <header className="h-14 shrink-0 bg-slate-900 text-white flex items-center gap-3 px-4">
        <button onClick={() => navigate("/epos")} data-testid="epos-register-exit"
          className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Exit
        </button>
        <div className="h-5 w-px bg-slate-700" />
        <div className="font-semibold whitespace-nowrap">{register?.name}</div>
        {session
          ? <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300" data-testid="epos-session-status">OPEN</span>
          : zReport
          ? <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-500/20 text-slate-300" data-testid="epos-session-status">CLOSED</span>
          : <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300" data-testid="epos-session-status">NO SESSION</span>}
        {session && canSell && (
          <div className="flex items-center gap-2 ml-1">
            <button onClick={openCash} data-testid="epos-cash-mgmt-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold border border-slate-700">
              <Banknote className="w-4 h-4 text-emerald-400" /> Cash
            </button>
            <button onClick={openCloseFlow} data-testid="epos-close-register-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold">
              <Lock className="w-4 h-4" /> Close register
            </button>
          </div>
        )}
        {!session && zReport && (
          <button onClick={() => setZReport(zReport)} data-testid="epos-view-zreport-btn"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold ml-1">
            <FileText className="w-4 h-4" /> View Z-report
          </button>
        )}
        <div className="ml-auto text-xs text-slate-400 whitespace-nowrap hidden md:block">
          {cfg?.stores?.find((s) => s.id === register?.store_id)?.name}
          {source && <span className="ml-2 text-slate-500">· {source}</span>}
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Catalogue */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Prominent scanner-first search bar */}
          <div className="shrink-0 bg-slate-900 px-3 py-3 border-b border-slate-800" data-testid="epos-scan-bar">
            <div className="relative max-w-3xl mx-auto">
              <ScanLine className="w-6 h-6 text-indigo-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input ref={searchRef} value={q} autoFocus
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleScan(); } }}
                placeholder="Scan barcode or search by name / SKU…"
                data-testid="epos-product-search"
                className="w-full h-16 pl-14 pr-28 rounded-2xl bg-white text-slate-900 placeholder-slate-400 text-xl font-medium shadow-lg focus:outline-none focus:ring-4 focus:ring-indigo-400/60" />
              {q && (
                <button onClick={() => { setQ(""); searchRef.current?.focus(); }}
                  data-testid="epos-search-clear"
                  className="absolute right-24 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  <X className="w-6 h-6" />
                </button>
              )}
              <button onClick={handleScan} data-testid="epos-scan-add-btn"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-11 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold inline-flex items-center gap-1.5">
                <ScanLine className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              {[["", "All products"], ["__bundles__", "Bundles"]].map(([k, l]) => (
                <button key={k || "all"} onClick={() => setActiveCat(k)} data-testid={`epos-filter-${k ? "bundles" : "all"}`}
                  className={`h-9 px-4 rounded-full text-sm font-semibold ${activeCat === k ? "bg-white text-slate-900" : "bg-white/15 text-white hover:bg-white/25"}`}>{l}</button>))}
            </div>
            <div className="max-w-3xl mx-auto mt-2 flex items-center gap-3 text-[11px] text-slate-400">
              <button onClick={toggleSound} data-testid="epos-sound-toggle" title="Toggle scanner sound"
                className="ml-auto inline-flex items-center gap-1 hover:text-white">
                {soundOn ? <Volume2 className="w-3.5 h-3.5 text-indigo-400" /> : <VolumeX className="w-3.5 h-3.5 text-slate-500" />}
                Sound {soundOn ? "on" : "off"}
              </button>
              <span className="inline-flex items-center gap-1" data-testid="epos-scanner-active"><ScanLine className="w-3.5 h-3.5 text-indigo-400" /> Scanner active</span>
            </div>
            {lastScan && (
              <div data-testid="epos-scan-status" data-scan-status={lastScan.status}
                className={`max-w-3xl mx-auto mt-2 px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${
                  lastScan.status === "success" ? "bg-emerald-600 text-white"
                  : lastScan.status === "warn" ? "bg-amber-500 text-white"
                  : "bg-rose-600 text-white"}`}>
                {lastScan.status === "success" ? <BadgeCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {lastScan.msg}
              </div>
            )}
          </div>

          {/* Quick keys */}
          {quickKeys.length > 0 && (
            <div className="shrink-0 bg-white border-b border-slate-200 px-3 py-2" data-testid="epos-quick-keys">
              <div className="flex items-center gap-2 overflow-x-auto">
                <span className="text-[11px] font-bold uppercase text-amber-500 flex items-center gap-1 shrink-0"><Star className="w-3.5 h-3.5" /> Quick keys</span>
                {quickKeys.map((k) => (
                  <button key={k.id} onClick={() => addQuickKey(k)} disabled={!canSell}
                    data-testid={`epos-quick-key-${k.id}`}
                    className="shrink-0 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                    {k.title}{k.price != null && <span className="ml-1.5 text-amber-600">{money(k.price)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Product grid */}
          <div className="flex-1 overflow-y-auto p-3">
            {loadingProducts && products.length === 0 ? (
              <div className="text-slate-400 text-sm py-16 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading catalogue…</div>
            ) : products.length === 0 ? (
              <div className="text-slate-400 text-sm py-16 text-center" data-testid="epos-products-empty">No products found.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="epos-product-grid">
                  {products.map((p, idx) => {
                    const out = isOut(p.stock_status);
                    const noPrice = p.price_missing || p.price == null || Number(p.price) <= 0;
                    return (
                      <button key={`${p.wc_id || p.sku}-${idx}`} onClick={() => addProduct(p)} disabled={!canSell || out || noPrice}
                        data-testid={`epos-product-${p.wc_id || p.sku || idx}`}
                        className="group relative text-left rounded-xl border border-slate-200 bg-white p-2.5 hover:border-indigo-300 hover:shadow-md transition disabled:cursor-not-allowed disabled:opacity-60 flex flex-col">
                        <div className="relative">
                          <ProductImage src={p.image} alt={p.name} />
                          {out && <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-600 text-white"><PackageX className="w-3 h-3" /> OUT</span>}
                          {!out && noPrice && <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white" data-testid={`epos-noprice-${p.wc_id || p.sku || idx}`}><AlertTriangle className="w-3 h-3" /> No price</span>}
                          {p.type_variable && <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-600 text-white"><Layers className="w-3 h-3" /> Options</span>}
                          {p.is_bundle && <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-600 text-white" data-testid={`epos-bundle-badge-${p.htss_legacy_id || idx}`}><Layers className="w-3 h-3" /> Bundle</span>}
                          {canManage && (
                            <span onClick={(e) => pinToQuickKeys(p, e)} title="Add to quick keys"
                              className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 border border-slate-200 hidden group-hover:flex items-center justify-center text-amber-500 hover:bg-amber-50">
                              <Star className="w-4 h-4" />
                            </span>
                          )}
                        </div>
                        <div className="mt-2 text-[13px] font-medium text-slate-800 line-clamp-2 min-h-[2.4rem] leading-snug">{p.name}</div>
                        <div className="mt-auto pt-1.5 flex items-baseline gap-1.5">
                          {noPrice
                            ? <span className="font-bold text-amber-600">No price</span>
                            : p.on_sale && p.regular_price
                            ? <><span className="font-bold text-rose-600">{money(p.price)}</span><span className="text-xs text-slate-400 line-through">{money(p.regular_price)}</span></>
                            : <span className="font-bold text-indigo-700">{p.type_variable ? `from ${money(p.price)}` : money(p.price)}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {page < totalPages && !q && (
                  <div className="flex justify-center mt-4">
                    <button onClick={loadMore} disabled={loadingProducts} data-testid="epos-load-more"
                      className="px-5 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50">
                      {loadingProducts ? "Loading…" : `Load more (${page}/${totalPages})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Basket */}
        <aside className="w-[420px] lg:w-[480px] shrink-0 bg-white border-l border-slate-200 flex flex-col" data-testid="epos-basket">
          <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-100 font-semibold text-slate-800 text-lg">
            <ShoppingCart className="w-6 h-6 text-indigo-600" /> Basket
            <span className="ml-auto text-sm text-slate-400" data-testid="epos-basket-count">{totals.count} item(s)</span>
            {basket.length > 0 && <button onClick={() => setBasket([])} className="text-sm text-slate-400 hover:text-rose-500 ml-3">Clear</button>}
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {basket.length === 0 ? (
              <div className="text-slate-400 text-lg text-center py-14">Tap a product to add it.</div>
            ) : basket.map((x) => (
              <div key={x.key} className="rounded-lg border border-slate-100 p-3 flex gap-3" data-testid={`epos-basket-line-${x.key}`}>
                <div className="w-14 shrink-0"><ProductImage src={x.image} alt={x.name} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <div className="text-base font-medium text-slate-800 line-clamp-2">{x.name}</div>
                    <button onClick={() => removeLine(x.key)} className="text-slate-300 hover:text-rose-500 shrink-0"><Trash2 className="w-5 h-5" /></button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setQty(x.key, -1)} data-testid={`epos-qty-dec-${x.key}`} className="w-9 h-9 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Minus className="w-4 h-4" /></button>
                      <span className="text-lg w-8 text-center tabular-nums font-medium" data-testid={`epos-qty-${x.key}`}>{x.qty}</span>
                      <button onClick={() => setQty(x.key, 1)} data-testid={`epos-qty-inc-${x.key}`} className="w-9 h-9 rounded border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Plus className="w-4 h-4" /></button>
                    </div>
                    <div className="font-semibold text-slate-800 text-lg">{money((x.price || 0) * x.qty)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 p-5 space-y-4">
            {sessionTotals && (
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-3 text-sm text-slate-500 grid grid-cols-3 gap-2" data-testid="epos-session-totals">
                <div>Txns <span className="font-bold text-slate-700 block tabular-nums text-lg">{sessionTotals.transactions}</span></div>
                <div>Cash <span className="font-bold text-slate-700 block tabular-nums text-lg">{money(sessionTotals.cash_sales)}</span></div>
                <div>Card <span className="font-bold text-slate-700 block tabular-nums text-lg">{money(sessionTotals.card_sales)}</span></div>
                <div>Sales <span className="font-bold text-slate-700 block tabular-nums text-lg">{money(sessionTotals.total_sales)}</span></div>
                <div>Disc <span className="font-bold text-slate-700 block tabular-nums text-lg">{money(sessionTotals.discounts)}</span></div>
                <div>VAT <span className="font-bold text-slate-700 block tabular-nums text-lg">{money(sessionTotals.vat)}</span></div>
              </div>
            )}
            <div className="flex justify-between text-xl">
              <span className="text-slate-500">Subtotal</span>
              <span className="font-bold text-slate-900" data-testid="epos-basket-subtotal">{money(totals.subtotal)}</span>
            </div>
            <button onClick={() => setCheckoutOpen(true)} disabled={!canSell || basket.length === 0 || !session}
              data-testid="epos-checkout-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-lg text-lg font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
              <ShoppingCart className="w-5 h-5" /> Checkout (TEST MODE)
            </button>
            <p className="text-sm text-amber-600 text-center">TEST MODE — simulated payments only. No real payment, stock or WooCommerce order.</p>
          </div>
        </aside>
      </div>

      {/* Variation modal */}
      {variationFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setVariationFor(null)} data-testid="epos-variation-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-100">
              <div className="font-bold text-slate-900 line-clamp-1">{variationFor.name}</div>
              <button onClick={() => setVariationFor(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {loadingVars ? (
                <div className="text-slate-400 text-sm py-8 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading options…</div>
              ) : variations.length === 0 ? (
                <div className="text-slate-400 text-sm py-8 text-center">No options available.</div>
              ) : variations.map((v) => {
                const out = isOut(v.stock_status);
                return (
                  <button key={v.wc_variation_id} onClick={() => addVariation(variationFor, v)} disabled={out}
                    data-testid={`epos-variation-${v.wc_variation_id}`}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 text-left disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="w-12 shrink-0"><ProductImage src={v.image} alt={v.name} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 line-clamp-2">{v.name}</div>
                      {out && <span className="text-[10px] font-bold text-rose-600">OUT OF STOCK</span>}
                    </div>
                    <div className="font-bold text-indigo-700 shrink-0">{money(v.price)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Cash management modal */}
      {cashOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setCashOpen(false)} data-testid="epos-cash-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[86vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-100">
              <div className="font-bold text-slate-900 flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-600" /> Cash management</div>
              <button onClick={() => setCashOpen(false)} data-testid="epos-cash-close"><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">Session<span className="block font-bold text-slate-800">{session?.session_ref || "—"}</span></div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">Opening float<span className="block font-bold text-slate-800" data-testid="epos-cash-opening-float">{money(recon?.opening_float)}</span></div>
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">Expected cash<span className="block font-bold text-emerald-700" data-testid="epos-cash-expected">{money(recon?.expected_cash)}</span></div>
              </div>

              <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="epos-cash-movement-form">
                <div className="flex gap-2">
                  <button onClick={() => setMovementForm((f) => ({ ...f, type: "cash_in" }))} data-testid="epos-cash-in-tab"
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold border ${movementForm.type === "cash_in" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>
                    <ArrowDownCircle className="w-4 h-4" /> Cash in
                  </button>
                  <button onClick={() => setMovementForm((f) => ({ ...f, type: "cash_out" }))} data-testid="epos-cash-out-tab"
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold border ${movementForm.type === "cash_out" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200"}`}>
                    <ArrowUpCircle className="w-4 h-4" /> Cash out
                  </button>
                </div>
                <label className="block text-xs text-slate-500">Amount (£)
                  <input type="number" min="0" step="0.01" value={movementForm.amount}
                    onChange={(e) => setMovementForm((f) => ({ ...f, amount: e.target.value }))}
                    data-testid="epos-cash-amount" placeholder="0.00"
                    className="block w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm" />
                </label>
                <label className="block text-xs text-slate-500">Reason <span className="text-rose-500">*</span>
                  <input type="text" value={movementForm.reason}
                    onChange={(e) => setMovementForm((f) => ({ ...f, reason: e.target.value }))}
                    data-testid="epos-cash-reason" placeholder="e.g. Float top-up, petty cash"
                    className="block w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm" />
                </label>
                <button onClick={submitMovement} disabled={savingMovement} data-testid="epos-cash-submit"
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50">
                  {savingMovement ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />} Record movement
                </button>
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-slate-400 mb-2">Movements this session</div>
                {reconLoading ? (
                  <div className="text-slate-400 text-sm py-4 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : (recon?.cash_movements?.length ? (
                  <div className="space-y-1.5" data-testid="epos-cash-movements-list">
                    {recon.cash_movements.map((m) => (
                      <div key={m.id} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-100">
                        {m.type === "cash_in" ? <ArrowDownCircle className="w-4 h-4 text-emerald-600 shrink-0" /> : <ArrowUpCircle className="w-4 h-4 text-rose-600 shrink-0" />}
                        <span className="font-medium text-slate-700 min-w-0 truncate">{m.reason}</span>
                        <span className="text-slate-400 text-xs truncate hidden sm:inline">· {m.operator_name}</span>
                        <span className={`ml-auto font-bold tabular-nums ${m.type === "cash_in" ? "text-emerald-700" : "text-rose-700"}`}>{m.type === "cash_in" ? "+" : "−"}{money(m.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-slate-400 text-sm py-3 text-center" data-testid="epos-cash-movements-empty">No cash movements yet.</div>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close register modal */}
      {closeOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !closing && setCloseOpen(false)} data-testid="epos-close-modal">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-100">
              <div className="font-bold text-slate-900 flex items-center gap-2"><Lock className="w-5 h-5 text-rose-600" /> Close register</div>
              <button onClick={() => !closing && setCloseOpen(false)} data-testid="epos-close-cancel"><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="text-sm text-slate-500">{register?.name} · <b className="text-slate-700">{session?.session_ref}</b></div>
              {reconLoading || !recon ? (
                <div className="text-slate-400 text-sm py-6 text-center flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading reconciliation…</div>
              ) : (
                <>
                  <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="epos-close-breakdown">
                    {[["Opening float", recon.opening_float], ["Cash sales", recon.cash_sales],
                      ["Cash refunds", -recon.cash_refunds], ["Cash in", recon.cash_in],
                      ["Cash out", -recon.cash_out]].map(([label, val]) => (
                      <div key={label} className="flex justify-between px-3 py-2">
                        <span className="text-slate-500">{label}</span>
                        <span className="tabular-nums text-slate-700">{money(val)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between px-3 py-2.5 bg-emerald-50">
                      <span className="font-semibold text-emerald-800">Expected cash</span>
                      <span className="tabular-nums font-bold text-emerald-800" data-testid="epos-close-expected">{money(recon.expected_cash)}</span>
                    </div>
                  </div>
                  <label className="block text-xs text-slate-500">Counted cash (£) <span className="text-rose-500">*</span>
                    <input type="number" min="0" step="0.01" value={countedCash} autoFocus
                      onChange={(e) => { setCountedCash(e.target.value); setConfirmClose(false); }}
                      data-testid="epos-counted-cash" placeholder="0.00"
                      className="block w-full mt-1 h-11 rounded-lg border border-slate-200 px-3 text-base font-medium" />
                  </label>
                  {countedCash !== "" && !isNaN(Number(countedCash)) && (() => {
                    const disc = Number(countedCash) - Number(recon.expected_cash || 0);
                    const off = Math.abs(disc) > 0.005;
                    return (
                      <div className={`rounded-lg px-3 py-2.5 text-sm flex justify-between ${off ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="epos-close-discrepancy">
                        <span className="font-semibold">{off ? "Discrepancy" : "Balanced"}</span>
                        <span className="tabular-nums font-bold">{disc >= 0 ? "+" : "−"}{money(Math.abs(disc))}</span>
                      </div>
                    );
                  })()}
                  {countedCash !== "" && Math.abs(Number(countedCash) - Number(recon.expected_cash || 0)) > 0.005 && (
                    <label className="block text-xs text-slate-500">Reason for discrepancy <span className="text-rose-500">*</span>
                      <input type="text" value={discrepancyReason}
                        onChange={(e) => setDiscrepancyReason(e.target.value)}
                        data-testid="epos-discrepancy-reason" placeholder="Explain the difference"
                        className="block w-full mt-1 h-10 rounded-lg border border-slate-200 px-3 text-sm" />
                    </label>
                  )}
                  {!confirmClose ? (
                    <button onClick={() => setConfirmClose(true)}
                      disabled={countedCash === "" || (Math.abs(Number(countedCash) - Number(recon.expected_cash || 0)) > 0.005 && !discrepancyReason.trim())}
                      data-testid="epos-close-review-btn"
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-base font-semibold disabled:opacity-40">
                      <Lock className="w-4 h-4" /> Review &amp; close
                    </button>
                  ) : (
                    <div className="space-y-2" data-testid="epos-close-confirm">
                      <p className="text-sm text-slate-600 text-center">This will close <b>{session?.session_ref}</b> and finalise the Z-report. No further sales can be taken on this session.</p>
                      <button onClick={doClose} disabled={closing} data-testid="epos-close-confirm-btn"
                        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-base font-semibold disabled:opacity-50">
                        {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm close
                      </button>
                      <button onClick={() => setConfirmClose(false)} disabled={closing}
                        className="w-full h-9 rounded-lg text-sm text-slate-500 hover:text-slate-800">Back</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Z-report modal */}
      {zReport && !closeOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="epos-zreport-modal">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-100">
              <div className="font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> Z-Report {zReport.session_ref}</div>
              <button onClick={() => setZReport(null)} data-testid="epos-zreport-close"><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 text-sm">
              <div className="text-slate-500 text-xs">Closed {zReport.closed_at} · {zReport.closed_by_name}</div>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {[["Transactions", String(zReport.transactions)], ["Gross sales", money(zReport.gross_sales)],
                  ["VAT", money(zReport.vat)], ["Discounts", money(zReport.discounts)],
                  ["Refunds", money(zReport.refunds)], ["Net sales", money(zReport.net_sales)],
                  ["Cash sales", money(zReport.cash_sales)], ["Card sales", money(zReport.card_sales)],
                  ["Expected cash", money(zReport.expected_cash)],
                  ["Counted cash", zReport.counted_cash != null ? money(zReport.counted_cash) : "—"],
                  ["Discrepancy", zReport.cash_discrepancy != null ? money(zReport.cash_discrepancy) : "—"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between px-3 py-2">
                    <span className="text-slate-500">{k}</span><span className="tabular-nums font-medium text-slate-800">{v}</span>
                  </div>
                ))}
              </div>
              {zReport.discrepancy_reason && <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">Reason: {zReport.discrepancy_reason}</div>}
            </div>
            <div className="border-t border-slate-100 p-4 flex flex-wrap gap-2">
              <button onClick={printZreport} data-testid="epos-zreport-print-btn"
                className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1.5 h-10 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={downloadZpdf} data-testid="epos-zreport-pdf-btn"
                className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1.5 h-10 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
                <Download className="w-4 h-4" /> PDF
              </button>
              <button onClick={emailZreport} data-testid="epos-zreport-email-btn"
                className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1.5 h-10 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
                <Mail className="w-4 h-4" /> Email
              </button>
              <button onClick={() => navigate("/epos")} data-testid="epos-zreport-done-btn"
                className="flex-1 min-w-[90px] inline-flex items-center justify-center gap-1.5 h-10 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">
                <CheckCircle2 className="w-4 h-4" /> Done
              </button>
            </div>
          </div>
        </div>
      )}

      <EposCheckout open={checkoutOpen} onClose={() => setCheckoutOpen(false)} basket={basket}
        registerId={registerId} cfg={cfg}
        onComplete={(txn) => { setCheckoutOpen(false); setBasket([]); setReceipt(txn); loadSessionTotals(); }} />
      <EposReceipt txn={receipt} company={{ name: cfg?.stores?.find((s) => s.id === register?.store_id)?.name ? "The Hot Tub Superstore" : "The Hot Tub Superstore" }}
        onClose={() => setReceipt(null)} />
    </div>
  );
}
